import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { apiGet, currentSession, KbError } from '../api/client'
import type { Envelope } from '../api/types'

type Summary = { revision_pending: number; archive_pending: number; total: number }
const EMPTY: Summary = { revision_pending: 0, archive_pending: 0, total: 0 }
const Context = createContext({ ...EMPTY, refresh: () => {} })

export function ReviewNotifications({ enabled, sessionId, children }: { enabled: boolean; sessionId: string; children: ReactNode }) {
  const [summary, setSummary] = useState(EMPTY)
  const refreshRef = useRef(() => {})
  useEffect(() => {
    setSummary(EMPTY)
    if (!enabled) return
    let active = true, running = false, queued = false
    let debounce: number | undefined
    const refresh = async () => {
      if (!active) return
      if (running) { queued = true; return }
      running = true
      try {
        const result = await apiGet<Envelope<Summary>>('/review-summary')
        if (active && currentSession()?.sessionId === sessionId) setSummary(result.data)
      } catch (error) {
        if (active && error instanceof KbError && [401, 403].includes(error.status)) setSummary(EMPTY)
      } finally {
        running = false
        if (active && queued) { queued = false; void refresh() }
      }
    }
    const visibleRefresh = () => { if (!document.hidden) void refresh() }
    const changed = () => { window.clearTimeout(debounce); debounce = window.setTimeout(() => void refresh(), 150) }
    refreshRef.current = () => { void refresh() }
    void refresh()
    const timer = window.setInterval(visibleRefresh, 15000)
    window.addEventListener('focus', visibleRefresh)
    document.addEventListener('visibilitychange', visibleRefresh)
    window.addEventListener('kb:reviews-changed', changed)
    return () => {
      active = false; refreshRef.current = () => {}
      window.clearInterval(timer); window.clearTimeout(debounce)
      window.removeEventListener('focus', visibleRefresh)
      document.removeEventListener('visibilitychange', visibleRefresh)
      window.removeEventListener('kb:reviews-changed', changed)
    }
  }, [enabled, sessionId])
  return <Context.Provider value={{ ...(enabled ? summary : EMPTY), refresh: () => refreshRef.current() }}>{children}</Context.Provider>
}

export const useReviewNotifications = () => useContext(Context)

export function PendingCount({ count, label }: { count: number; label: string }) {
  return count > 0 ? <span className="pending-count" aria-label={`${label} ${count} 条待处理`} title={`${label}：${count} 条待处理`}>{count > 99 ? '99+' : count}</span> : null
}
